import type { SearchCriteria, SearchCondition, SearchOperation, ExtendedListParams } from '@/types/search-criteria.ts';
import type { ColumnFiltersState, SortingState } from '@tanstack/react-table';

/**
 * Утилита для работы с критериями поиска 
 */
export class SearchCriteriaBuilder {
  public criteria: SearchCriteria;

  constructor(mode: 'and' | 'or' = 'and') {
    this.criteria = {
      conditions: [],
      mode
    };
  }

  /**
   * Добавляет простое условие поиска
   */
  addCondition(field: string, operation: SearchOperation, value: any): this {
    this.criteria.conditions.push([field, operation, value]);
    return this;
  }

  /**
   * Добавляет группу условий
   */
  addGroup(criteria: SearchCriteria): this {
    this.criteria.conditions.push(criteria);
    return this;
  }

  /**
   * Создает новую группу условий с указанным режимом
   */
  createGroup(mode: 'and' | 'or' = 'and'): SearchCriteriaBuilder {
    return new SearchCriteriaBuilder(mode);
  }

  /**
   * Возвращает построенные критерии
   */
  build(): SearchCriteria {
    return this.criteria;
  }

  /**
   * Кодирует критерии в base64 для передачи в API
   */
  toBase64(): string {
    const json = JSON.stringify(this.criteria);
    return btoa(unescape(encodeURIComponent(json)));
  }

  /**
   * Создает критерии из base64 строки
   */
  static fromBase64(base64String: string): SearchCriteria {
    try {
      const json = decodeURIComponent(escape(atob(base64String)));
      return JSON.parse(json) as SearchCriteria;
    } catch {
      throw new Error('Невалидная base64 строка с критериями поиска');
    }
  }

  /**
   * Статический метод для быстрого создания критериев
   */
  static create(mode: 'and' | 'or' = 'and'): SearchCriteriaBuilder {
    return new SearchCriteriaBuilder(mode);
  }
}

/**
 * Универсальные методы для работы с параметрами запросов
 */
export class ApiHelpers {
  /**
   * Строит query параметры из ExtendedListParams
   */
  static buildQueryParams(params?: ExtendedListParams): URLSearchParams {
    const queryParams = new URLSearchParams();
    
    // Если есть критерии поиска, кодируем их в base64 и добавляем в search
    if (params?.criteria) {
      const json = JSON.stringify(params.criteria);
      const base64 = btoa(unescape(encodeURIComponent(json)));
      queryParams.append('search', base64);
    } else if (params?.search) {
      queryParams.append('search', params.search);
    }
    
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.count) queryParams.append('count', params.count.toString());
    if (params?.sort) {
      params.sort.forEach(sort => queryParams.append('sort[]', sort));
    }

    if (params?.totalOnly) {
      queryParams.append('totalOnly', 'true');
    }

    return queryParams;
  }

  /**
   * Строит URL с query параметрами
   */
  static buildUrl(baseUrl: string, params?: ExtendedListParams): string {
    const queryParams = this.buildQueryParams(params);
    return `${baseUrl}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  }
}

/**
 * Опции для построения глобального поиска как части criteria
 */
export interface GlobalSearchOptions {
  globalSearchFields?: string[]
  globalSearchMode?: 'and' | 'or'
  buildGlobalSearchCriteria?: (text: string) => SearchCriteria | undefined
}

/**
 * Хелперы для создания популярных условий поиска
 */
export const SearchHelpers = {
  /**
   * Поиск по точному совпадению
   */
  equals: (field: string, value: any): SearchCondition => [field, 'eq', value],

  /**
   * Поиск по списку значений (IN)
   */
  inList: (field: string, values: any[]): SearchCondition => [field, 'eq', values],

  /**
   * Поиск по частичному совпадению
   */
  like: (field: string, pattern: string): SearchCondition => [field, 'like', pattern],

  /**
   * Поиск по частичному совпадению (с автоматическими %)
   */
  contains: (field: string, text: string): SearchCondition => [field, 'ilike', `%${text}%`],

  /**
   * Поиск больше чем
   */
  greaterThan: (field: string, value: any): SearchCondition => [field, 'gt', value],

  /**
   * Поиск меньше чем
   */
  lessThan: (field: string, value: any): SearchCondition => [field, 'lt', value],

  /**
   * Поиск в диапазоне
   */
  between: (field: string, min: any, max: any): SearchCondition => [field, 'between', [min, max]],

  /**
   * Поиск не равно
   */
  notEquals: (field: string, value: any): SearchCondition => [field, 'ne', value],

  /**
   * Поиск не в списке
   */
  notInList: (field: string, values: any[]): SearchCondition => [field, 'nin', values],

  /**
   * JSON поиск по равенству
   */
  jsonEquals: (field: string, value: any): SearchCondition => [field, 'json_eq', value],

  /**
   * JSON поиск по вхождению
   */
  jsonContains: (field: string, value: any): SearchCondition => [field, 'json_in', value],

  /**
   * Создает критерии для поиска по нескольким полям (OR)
   */
  searchAcrossFields: (searchText: string, fields: string[]): SearchCriteria => {
    const conditions = fields.map(field => 
      SearchHelpers.contains(field, searchText)
    );
    
    return {
      conditions,
      mode: 'or'
    };
  },

  /**
   * Создает условие для поиска активных записей
   */
  onlyActive: (field: string = 'active'): SearchCondition => [field, 'eq', true],

  /**
   * Создает условие для поиска неактивных записей
   */
  onlyInactive: (field: string = 'active'): SearchCondition => [field, 'eq', false],

  /**
   * Создает условие для поиска по дате создания (после указанной даты)
   */
  createdAfter: (date: string, field: string = 'createdAt'): SearchCondition => [field, 'gte', date],

  /**
   * Создает условие для поиска по дате создания (до указанной даты)
   */
  createdBefore: (date: string, field: string = 'createdAt'): SearchCondition => [field, 'lte', date],

  /**
   * Создает критерии для поиска в диапазоне дат
   */
  dateRange: (startDate: string, endDate: string, field: string = 'createdAt'): SearchCriteria => {
    return SearchCriteriaBuilder
      .create('and')
      .addCondition(field, 'gte', startDate)
      .addCondition(field, 'lte', endDate)
      .build();
  }
};

/**
 * Утилита для построения критериев из фильтров таблицы
 */
export const TableFiltersHelper = {
  buildCriteriaFromFilters: (filters: ColumnFiltersState): SearchCriteria | undefined => {
    if (filters.length === 0) return undefined;
    
    const builder = SearchCriteriaBuilder.create('and');
    filters.forEach(filter => {
      if (filter.value === undefined || filter.value === '') {
        return;
      }

      if (Array.isArray(filter.value) && filter.value.length > 1) {
        builder.addCondition(filter.id, 'eq', filter.value);
      } else {
        if (Array.isArray(filter.value)) {
          const {operator, value} = determineOperator(filter.value[0]);
          builder.addCondition(filter.id, operator, value);
        } else {
          const {operator, value} = determineOperator(filter.value);
          builder.addCondition(filter.id, operator, value);
        }
      }
    });
    return builder.build();
  },

  buildSortFromState: (sorting: SortingState): string[] | undefined => {
    if (sorting.length === 0) return undefined;
    return sorting.map(sort => `${sort.id} ${sort.desc ? 'desc' : 'asc'}`);
  }
};

/**
 * Автоматически определяет оператор на основе значения фильтра
 */
function determineOperator(value: any): { operator: SearchOperation, value: any } {
  if (typeof value === 'string') {
    if (value.startsWith('!')) {
      return { operator: 'neq', value: value.slice(1) };
    }
    if (value.startsWith('>=')) {
      return { operator: 'gte', value: value.slice(2) };
    }
    if (value.startsWith('<=')) {
      return { operator: 'lte', value: value.slice(2) };
    }
    if (value.startsWith('>')) {
      return { operator: 'gt', value: value.slice(1) };
    }
    if (value.startsWith('<')) {
      return { operator: 'lt', value: value.slice(1) };
    }
    if (value.includes('%')) {
      return { operator: 'like', value: value.slice(1) };
    }
    if (value.includes('*')) {
      return { operator: 'ilike', value: value.slice(1) };
    }
    return { operator: 'eq', value: value };
  }
  
  if (typeof value === 'number') {
    return { operator: 'eq', value: value };
  }
  
  if (typeof value === 'boolean') {
    return { operator: 'eq', value: value };
  }
  
  if (value instanceof Date) {
    return { operator: 'eq', value: value };
  }
  
  return { operator: 'eq', value: value };
}

/**
 * Утилита для работы с пагинацией и серверным состоянием таблицы
 */
export const PaginationHelper = {
  createPaginationHandlers: (
    setCurrentPage: (page: number) => void,
    setPageSize: (size: number) => void,
    setSorting: (sorting: SortingState) => void,
    setFilters: (filters: ColumnFiltersState) => void,
    setGlobalFilter: (filter: string) => void
  ) => ({
    onPaginationChange: (page: number, size: number) => {
      setCurrentPage(page);
      setPageSize(size);
    },
    onSortingChange: (newSorting: SortingState) => {
      setSorting(newSorting);
      setCurrentPage(0);
    },
    onFiltersChange: (newFilters: ColumnFiltersState) => {
      setFilters(newFilters);
      setCurrentPage(0);
    },
    onGlobalFilterChange: (newGlobalFilter: string) => {
      setGlobalFilter(newGlobalFilter);
      setCurrentPage(0);
    },
  }),

  buildRequestParams: (
    currentPage: number,
    pageSize: number,
    sorting: SortingState,
    filters: ColumnFiltersState,
    globalFilter: string,
    additionalParams?: Partial<ExtendedListParams>,
    searchOptions?: GlobalSearchOptions
  ): ExtendedListParams => {
    const criteriaFromFilters = TableFiltersHelper.buildCriteriaFromFilters(filters);
    const sort = TableFiltersHelper.buildSortFromState(sorting);

    let combinedCriteria: SearchCriteria | undefined = criteriaFromFilters;

    if (globalFilter && globalFilter.trim().length > 0) {
      let globalCriteria: SearchCriteria | undefined;

      if (searchOptions?.buildGlobalSearchCriteria) {
        globalCriteria = searchOptions.buildGlobalSearchCriteria(globalFilter.trim());
      } else if (searchOptions?.globalSearchFields && searchOptions.globalSearchFields.length > 0) {
        const conditions = searchOptions.globalSearchFields.map((field) =>
          SearchHelpers.contains(field, globalFilter.trim())
        );
        globalCriteria = {
          conditions,
          mode: searchOptions.globalSearchMode || 'or',
        };
      }

      if (globalCriteria) {
        if (combinedCriteria) {
          combinedCriteria = {
            conditions: [combinedCriteria, globalCriteria],
            mode: 'and',
          };
        } else {
          combinedCriteria = globalCriteria;
        }
      }
    }

    return {
      page: currentPage + 1,
      count: pageSize,
      sort,
      criteria: combinedCriteria,
      ...additionalParams,
    };
  }
};

